import { BIGCOMMERCE_HEALTHCHECK_RESPONSE } from "./integrationsBigCommerceHealthcheckResponse";
import { SHOPIFY_HEALTHCHECK_RESPONSE } from "./integrationsShopifyHealthcheckResponse";

/**
 * Inline MSW responses for the Integrations endpoints (All Integrations page).
 * Spread into INLINE_RESPONSES in inlineResponses.tsx.
 */
export const INTEGRATIONS_INLINE_RESPONSES: Record<
  string,
  Record<string, unknown> | unknown[]
> = {
  // The tags this store's customers carry, for the tag pickers (ENG-6685). Real-looking values on
  // purpose: the picker's whole job is to show a merchant's own vocabulary, and "tag-1, tag-2"
  // would document nothing about what that looks like.
  "/apollo/integrations/shopify-customer-tags": {
    tags: ["wholesale", "reseller", "vip", "net-30", "retail"],
  },

  // The store's own locations, for the warehouse picker (ENG-7040). Lenmark's real docks, and one
  // with no postal code -- that last one is the case the picker exists to surface, since a
  // warehouse without a lane can never match a quoted origin.
  "/apollo/integrations/shopify-locations": {
    locations: [
      {
        id: "gid://shopify/Location/1",
        name: "Langley",
        postalCode: "V2Y 1A1",
      },
      { id: "gid://shopify/Location/2", name: "Leduc", postalCode: "T9E 7N5" },
      { id: "gid://shopify/Location/3", name: "Nisku", postalCode: "T9E 7W1" },
      { id: "gid://shopify/Location/4", name: "Offsite" },
    ],
  },

  "/apollo/integrations/shopify-healthcheck": {
    ...SHOPIFY_HEALTHCHECK_RESPONSE,
  },

  "/apollo/integrations/bigcommerce-healthcheck": {
    ...BIGCOMMERCE_HEALTHCHECK_RESPONSE,
  },

  // A BigCommerce store's customer groups, for the exceptions pickers (ENG-7222). The groups a
  // wholesale store actually keeps, so the picker documents what a merchant sees.
  "/apollo/integrations/bigcommerce-customer-groups": {
    groups: [
      { id: 6, name: "Amazon" },
      { id: 2, name: "Retail" },
      { id: 4, name: "Wholesale" },
    ],
  },

  // A product search in the store's catalogue, for the excluded-products picker.
  "/apollo/integrations/bigcommerce-products": {
    products: [
      { id: 91, name: "Air compressor, 60 gal" },
      { id: 92, name: "Air compressor, 80 gal" },
    ],
  },

  // What repairing an unreadable config would reset: one bad limit, and a pallet base whose size
  // won't read. Shows that a default can be a value nobody wants (a 0" base), which is why the
  // dialog lists what each becomes.
  "/apollo/integrations/preview-config-repair": {
    resets: [
      {
        path: "maxHandlingUnits",
        storedValue: '"not a number"',
        resetTo: "4",
      },
      {
        path: "handlingTimeBusinessDays",
        storedValue: '"soon"',
        resetTo: "1",
      },
    ],
    resetsEverything: false,
    unlinksStore: false,
  },

  // All Integrations page — cross-company NetSuite, Shopify and BigCommerce connections.
  "/apollo/integrations/get-all-integrations": {
    netsuite: [
      {
        integration: {
          integrationId: "a1111111-0000-0000-0000-000000000001",
          companyId: "11111111-1111-1111-1111-111111111111",
          provider: "netsuite",
          name: "Radial NetSuite",
          environment: "production",
          enabled: true,
          lastSuccessfulCallAt: "2026-05-06T10:00:00Z",
          lastError: undefined,
          lastErrorAt: undefined,
          createdAt: "2026-01-10T12:00:00Z",
          updatedAt: "2026-05-06T10:00:00Z",
          companyName: "Radial Inc",
        },
        config: { accountId: "8026012" },
      },
      {
        integration: {
          integrationId: "a1111111-0000-0000-0000-000000000002",
          companyId: "22222222-2222-2222-2222-222222222222",
          provider: "netsuite",
          name: "Acme NetSuite (sandbox)",
          environment: "sandbox",
          enabled: true,
          lastSuccessfulCallAt: "2026-05-03T09:00:00Z",
          lastError: "HTTP 401 from RESTlet",
          lastErrorAt: "2026-05-06T09:00:00Z",
          createdAt: "2026-02-01T12:00:00Z",
          updatedAt: "2026-05-06T09:00:00Z",
          companyName: "Acme Distributing",
        },
        config: { accountId: "8026012-sb1" },
      },
    ],
    shopify: [
      {
        integration: {
          integrationId: "b2222222-0000-0000-0000-000000000001",
          companyId: "22222222-2222-2222-2222-222222222222",
          provider: "shopify",
          name: "acme-demo.myshopify.com",
          environment: "production",
          enabled: true,
          lastSuccessfulCallAt: "2026-05-06T11:40:00Z",
          lastError: undefined,
          lastErrorAt: undefined,
          createdAt: "2026-03-15T12:00:00Z",
          updatedAt: "2026-05-06T11:40:00Z",
          companyName: "Acme Distributing",
        },
        // Partial on purpose — this page shows a summary, not the whole config. The two rule lists
        // are here because the generated deserializer maps every required array without a null
        // guard, so omitting one throws and the page falls to its error boundary. That failure
        // shows up as a snapshot diff rather than a thrown test, so `vitest -u` will happily bake
        // the crash in as the baseline — which is how tagLocationTypeRules got missed once already.
        // Any future required array on ShopifyConfig has to be added here too.
        config: {
          shopDomain: "acme-demo.myshopify.com",
          tagAccessorialRules: [],
          tagLocationTypeRules: [],
          rateSelectors: [],
          allowedCarriers: [],
          blockedCarriers: [],
          pickupLocations: [],
        },
      },
    ],
    bigcommerce: [
      {
        integration: {
          integrationId: "c3333333-0000-0000-0000-000000000001",
          companyId: "33333333-3333-3333-3333-333333333333",
          provider: "bigcommerce",
          name: "x7k2m9qa",
          environment: "production",
          enabled: true,
          lastSuccessfulCallAt: "2026-09-29T16:05:00Z",
          lastError: undefined,
          lastErrorAt: undefined,
          createdAt: "2026-09-02T12:00:00Z",
          updatedAt: "2026-09-29T16:05:00Z",
          companyName: "Northwind Fixtures",
        },
        // Whole, unlike the Shopify entry above: every nested object here is required, and the
        // generated deserializer throws on a missing one.
        config: {
          storeHash: "x7k2m9qa",
          webhookSubscriptions: {},
          ratesEnabled: true,
          handlingTimeBusinessDays: 1,
          maxHandlingUnits: 4,
          maxRatesPerResponse: 3,
          rateSelectors: [],
          deliveryDefaults: { locationType: "Residential", accessorials: [] },
          pickupDefaults: { locationType: "Warehouse", accessorials: [] },
          palletization: {
            enabled: false,
            palletBases: [{ lengthInches: 48, widthInches: 40 }],
            maxPalletHeightInches: 65,
            maxPalletWeightPounds: 1000,
            palletTareWeightPounds: 40,
            itemsStackOnEachOther: true,
            palletsAreStackable: false,
          },
          requiredCustomerGroups: [],
          groupAccessorialRules: [],
          groupLocationTypeRules: [],
          excludedProducts: [],
          productHandlingTimes: [],
          markup: { percent: 0, flat: 0 },
        },
      },
    ],
    unreadable: [
      {
        integration: {
          integrationId: "d4444444-0000-0000-0000-000000000001",
          companyId: "44444444-4444-4444-4444-444444444444",
          provider: "shopify",
          name: "oldstore.myshopify.com",
          environment: "production",
          enabled: true,
          lastSuccessfulCallAt: "2026-08-14T10:00:00Z",
          createdAt: "2026-02-01T12:00:00Z",
          updatedAt: "2026-08-14T10:00:00Z",
          companyName: "Harbour Supply",
        },
        reason:
          "The stored shopify config can't be read (MismatchedInputException). Engineering has been alerted. Fix it with Repair config on the company's Integrations tab, which resets only the values that can't be read.",
      },
    ],
  },

  // NetSuite check-state — one row per drift verdict (in sync, drift, not in FS,
  // superseded) so the dialog table and every NetSuiteSyncColumns cell render.
  "/apollo/netsuite-sync/check-state": {
    checkedAt: "2026-05-06T14:30:00Z",
    truncated: false,
    rawResponse:
      '{"ok":true,"truncated":false,"shipments":[{"internalId":"48210"},{"internalId":"48219"}]}',
    comparisons: [
      {
        netsuite: {
          internalId: "48210",
          fsId: "c0ffee00-0000-4000-8000-000000000001",
          shipStatus: "val_fsmp_status_booked",
          isCurrent: true,
          cost: 412.55,
          sentAt: "2026-05-05T18:00:00Z",
          lastWebhookAt: "2026-05-06T09:15:00Z",
          bol: "FS-BOL-48210",
          owner: {
            id: "SO-991",
            type: "SalesOrd",
            tranId: "SO12841",
            orderStatus: "Pending Fulfillment",
            shippingCost: 412.55,
          },
          fulfillments: [
            {
              id: "IF-771",
              tranId: "IF88120",
              orderId: "SO-991",
              orderTranId: "SO12841",
              orderType: "SalesOrd",
            },
          ],
        },
        shipment: {
          shipmentId: "a1a1a1a1-1111-4111-8111-111111111111",
          state: "booking_confirmed",
        },
        presentInFreightSimple: true,
        expectedNetSuiteStatus: "val_fsmp_status_booked",
        inSync: true,
        statusDrift: false,
      },
      {
        netsuite: {
          internalId: "48219",
          fsId: "c0ffee00-0000-4000-8000-000000000002",
          shipStatus: "val_fsmp_status_in_transit",
          isCurrent: true,
          cost: 268.0,
          sentAt: "2026-05-04T12:00:00Z",
          lastWebhookAt: "2026-05-05T22:40:00Z",
          owner: {
            id: "SO-992",
            type: "SalesOrd",
            tranId: "SO12842",
            orderStatus: "Partially Fulfilled",
            shippingCost: 268.0,
          },
          fulfillments: [],
        },
        shipment: {
          shipmentId: "b2b2b2b2-2222-4222-8222-222222222222",
          state: "delivered",
        },
        presentInFreightSimple: true,
        expectedNetSuiteStatus: "val_fsmp_status_delivered",
        inSync: false,
        statusDrift: true,
      },
      {
        netsuite: {
          internalId: "48225",
          fsId: "c0ffee00-0000-4000-8000-000000000003",
          shipStatus: "val_fsmp_status_delivered",
          isCurrent: true,
          cost: 155.9,
          sentAt: "2026-05-03T08:00:00Z",
          owner: {
            id: "SO-993",
            type: "SalesOrd",
            tranId: "SO12843",
            orderStatus: "Fully Fulfilled",
            shippingCost: 155.9,
          },
          fulfillments: [],
        },
        shipment: undefined,
        presentInFreightSimple: false,
        expectedNetSuiteStatus: undefined,
        inSync: false,
        statusDrift: false,
      },
      {
        netsuite: {
          internalId: "48198",
          fsId: "c0ffee00-0000-4000-8000-000000000004",
          shipStatus: "val_fsmp_status_booked",
          isCurrent: false,
          cost: 402.0,
          sentAt: "2026-04-28T10:00:00Z",
          owner: {
            id: "SO-990",
            type: "SalesOrd",
            tranId: "SO12840",
            orderStatus: "Closed",
            shippingCost: 402.0,
          },
          fulfillments: [],
        },
        shipment: {
          shipmentId: "d4d4d4d4-4444-4444-8444-444444444444",
          state: "in_transit",
        },
        presentInFreightSimple: true,
        expectedNetSuiteStatus: "val_fsmp_status_in_transit",
        inSync: false,
        statusDrift: false,
      },
    ],
  },

  // NetSuite resync — result of pushing tracking IDs back to NetSuite.
  "/apollo/netsuite-sync/resync": {
    results: [
      {
        trackingId: "c0ffee00-0000-4000-8000-000000000002",
        outcome: "Resynced",
        detail: "Sent Delivered event to NetSuite",
      },
    ],
  },
};
